import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Wallet as WalletIcon, CreditCard, Coins, Plus, X, ArrowLeft,
  Gift, ShieldCheck, RotateCcw, Send, Lock, Info, BarChart3, AlertTriangle, FileText,
} from "lucide-react";
import api from "../api";
import toast from "react-hot-toast";
import Pagination from "../components/Pagination";

const TX_PER_PAGE = 10;

// Monthly subscription tiers — USD display, INR (≈ usd×85) charged at checkout.
const SUB_PLANS = [
  { tier: "STARTER", name: "Starter", usd: 25,  inr: 2125,  feats: ["7 scans / month", "Full remediation + code", "PDF · HTML · DOCX · CSV", "1 free OSINT / month"] },
  { tier: "PRO",     name: "Pro",     usd: 99,  inr: 8415,  popular: true, feats: ["30 scans / month", "Scheduled scans", "3 free OSINT / month", "Company-domain guard"] },
  { tier: "MAX",     name: "Max",     usd: 250, inr: 21250, feats: ["100 scans / month", "Scheduled scans", "3 free OSINT / month", "Priority support"] },
];

async function downloadInvoice(num) {
  try {
    const res = await api.get(`/api/credits/invoice/${num}?format=pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${num}.pdf`; a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Could not download receipt");
  }
}

export default function Wallet() {
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [txPage, setTxPage] = useState(0);
  const [packages, setPackages] = useState([]);
  const [razorpayKeyId, setRazorpayKeyId] = useState(null);
  const [busy, setBusy] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [user, setUser] = useState(null);
  const nav = useNavigate();

  // Detect brand from subdomain (kept — drives only the display name; colors come from Aurora tokens)
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const sub = hostname.split('.')[0];
  const brand = sub === 'debug' ? {
    name: "BugScout"
  } : sub === 'sip' ? {
    name: "SIP Tester"
  } : {
    name: "Aegis"
  };

  // Read the live Aurora accent so the (non-React) Razorpay checkout matches the theme.
  const accentColor = () => {
    if (typeof window === 'undefined') return "#1FD0A3";
    const v = getComputedStyle(document.documentElement).getPropertyValue('--au-accent').trim();
    return v || "#1FD0A3";
  };

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [b, t, p, u] = await Promise.all([
        api.get("/api/credits/balance"),
        api.get("/api/credits/transactions"),
        api.get("/api/credits/packages"),
        api.get("/api/auth/me")
      ]);
      setBalance(b.data.credits);
      setTransactions(t.data.transactions || []);
      setPackages(p.data.packages || []);
      setRazorpayKeyId(p.data.razorpay_key_id);
      setUser(u.data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load wallet");
    } finally { setBusy(false); }
  };

  const initiatePurchase = async (pkgId) => {
    if (!razorpayKeyId) {
      toast.error("Payment gateway not configured yet. Coming soon!");
      return;
    }

    setBusy(true);
    try {
      // Step 1: Create Razorpay order on backend
      const initRes = await api.post("/api/credits/purchase/init", { package_id: pkgId });
      const order = initRes.data;

      // Step 2: Load Razorpay script if not loaded
      if (!window.Razorpay) {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = "https://checkout.razorpay.com/v1/checkout.js";
          script.onload = resolve;
          script.onerror = reject;
          document.body.appendChild(script);
        });
      }

      // Step 3: Open Razorpay checkout
      const rzp = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "Khojo Credits",
        description: `${order.credits} credits`,
        prefill: {
          name: order.user_name,
          email: order.user_email
        },
        theme: { color: accentColor() },
        handler: async (response) => {
          // Step 4: Verify on backend
          try {
            const verifyRes = await api.post("/api/credits/purchase/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              package_id: pkgId
            });
            toast.success(`${verifyRes.data.credits_added} credits added!`);
            setShowAddModal(false);
            load(); // Refresh
          } catch (e) {
            toast.error("Payment verification failed. Contact support.");
          }
        },
        modal: {
          ondismiss: () => setBusy(false)
        }
      });
      rzp.open();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to start payment");
      setBusy(false);
    }
  };

  const ensureRazorpay = async () => {
    if (window.Razorpay) return;
    await new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = resolve; s.onerror = reject;
      document.body.appendChild(s);
    });
  };

  const subscribeToPlan = async (tier) => {
    if (!razorpayKeyId) { toast.error("Payment gateway not configured yet. Coming soon!"); return; }
    setBusy(true);
    try {
      const res = await api.post(`/api/razorpay/subscribe?plan=${tier}`);
      const sub = res.data;
      await ensureRazorpay();
      const rzp = new window.Razorpay({
        key: sub.razorpay_key_id,
        subscription_id: sub.subscription_id,    // recurring checkout (not order_id)
        name: `${brand.name} ${tier}`,
        description: `${tier} monthly subscription`,
        prefill: { name: user?.name, email: user?.email },
        theme: { color: accentColor() },
        handler: async () => {
          // Activate the plan immediately server-side (don't depend on the webhook,
          // which only handles renewals). Falls back to the webhook if this fails.
          try {
            const v = await api.post(`/api/razorpay/subscription/verify?subscription_id=${sub.subscription_id}`);
            toast.success(v.data?.ok ? `${tier} plan activated!` : "Payment received — your plan will activate shortly.");
          } catch {
            toast.success("Payment received — your plan will activate shortly.");
          }
          load();
          setBusy(false);
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start subscription");
      setBusy(false);
    }
  };

  const cancelSubscription = async () => {
    if (!window.confirm("Cancel your subscription at the end of the current billing cycle?")) return;
    try {
      await api.post("/api/razorpay/subscription/cancel?at_cycle_end=true");
      toast.success("Subscription will cancel at the cycle end.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    }
  };

  if (busy && balance === 0) {
    return (
      <div className="au-root" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: "var(--au-accent-ink)", fontWeight: 600 }}>Loading wallet...</div>
      </div>
    );
  }

  return (
    <div className="au-root">
      {/* Nav */}
      <nav style={{
        padding: "20px 32px", borderBottom: "1px solid var(--au-hair)",
        display: "flex", justifyContent: "space-between", alignItems: "center"
      }}>
        <Link to="/dashboard" style={{
          color: "var(--au-accent-ink)", textDecoration: "none", fontWeight: 700,
          fontSize: 16, display: "inline-flex", alignItems: "center", gap: 8
        }}>
          <ArrowLeft size={18} /> {brand.name}
        </Link>
        <div style={{ fontSize: 12, color: "var(--au-muted)" }}>{user?.email}</div>
      </nav>

      <div className="au-container" style={{ maxWidth: 900, padding: "40px 32px" }}>
        {/* Balance Card */}
        <div className="au-glass au-pad-lg" style={{ marginBottom: 28, textAlign: "center" }}>
          <div style={{
            fontSize: 12, color: "var(--au-muted)", letterSpacing: 3, marginBottom: 8,
            display: "inline-flex", alignItems: "center", gap: 8, justifyContent: "center"
          }}>
            <WalletIcon size={14} /> YOUR BALANCE
          </div>
          <div className="au-stat-num" style={{ fontSize: 56, lineHeight: 1, color: "var(--au-accent-ink)", margin: "8px 0" }}>
            {balance.toLocaleString()}
          </div>
          <div className="au-stat-lbl" style={{ marginBottom: 20 }}>credits available</div>
          <div>
            <button
              className="au-pill au-pill-primary"
              onClick={() => setShowAddModal(true)}>
              <Plus size={18} /> ADD CREDITS
            </button>
          </div>
        </div>

        {/* Subscription Plans */}
        <div className="au-glass au-pad" style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 18, marginTop: 0, marginBottom: 4, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
            <CreditCard size={20} style={{ color: "var(--au-accent-ink)" }} /> Subscription Plans
          </h2>
          <div style={{ fontSize: 12, color: "var(--au-muted)", marginBottom: 18 }}>
            Monthly tiers · prices in USD, charged in ₹ (INR) at checkout
            {user?.subscription_status && <> · status: <strong style={{ color: "var(--au-accent-ink)" }}>{user.subscription_status}</strong></>}
          </div>
          <div className="au-grid au-cols-3" style={{ gap: 12 }}>
            {SUB_PLANS.map(p => {
              const current = (user?.plan || "").toLowerCase().includes(p.tier.toLowerCase());
              return (
                <div key={p.tier} className="au-glass" style={{ padding: 18, borderRadius: "var(--au-r-sm)", border: p.popular ? "1px solid var(--au-accent)" : undefined, position: "relative" }}>
                  {p.popular && <div style={{ position: "absolute", top: -10, right: 12, background: "var(--au-accent)", color: "#04110d", fontSize: 10, fontWeight: 800, letterSpacing: 1, padding: "2px 8px", borderRadius: 20 }}>POPULAR</div>}
                  <div style={{ fontSize: 16, fontWeight: 700, color: "var(--au-text)" }}>{p.name}</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: "var(--au-accent-ink)", marginTop: 4 }}>${p.usd}<span style={{ fontSize: 12, color: "var(--au-muted)", fontWeight: 600 }}>/mo</span></div>
                  <div style={{ fontSize: 11, color: "var(--au-muted)", marginBottom: 12 }}>≈ ₹{p.inr.toLocaleString()} at checkout</div>
                  <ul style={{ listStyle: "none", padding: 0, margin: "0 0 14px", display: "flex", flexDirection: "column", gap: 6 }}>
                    {p.feats.map((f, i) => (
                      <li key={i} style={{ fontSize: 12, color: "var(--au-text-2)", display: "flex", gap: 6, alignItems: "flex-start" }}>
                        <ShieldCheck size={13} style={{ color: "var(--au-success)", flex: "none", marginTop: 2 }} /> {f}
                      </li>
                    ))}
                  </ul>
                  {current ? (
                    <button className="au-pill au-pill-glass au-pill-sm" disabled style={{ width: "100%", opacity: 0.7, cursor: "default" }}>Current plan</button>
                  ) : (
                    <button className="au-pill au-pill-primary au-pill-sm" style={{ width: "100%" }} disabled={busy || !razorpayKeyId} onClick={() => subscribeToPlan(p.tier)}>
                      {razorpayKeyId ? "Subscribe" : "Soon"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {user?.subscription_status === "active" && (
            <button onClick={cancelSubscription} className="au-pill au-pill-ghost au-pill-sm" style={{ marginTop: 14 }}>
              Cancel subscription
            </button>
          )}
        </div>

        {/* Quick Info */}
        <div className="au-grid au-cols-3" style={{ gap: 12, marginBottom: 32 }}>
          <InfoCard Icon={Gift} label="Daily Bonus" value="+10" hint="Free every day" />
          <InfoCard Icon={ShieldCheck} label="Rule-Based Scan" value="FREE" hint="0 credits" />
          <InfoCard Icon={RotateCcw} label="Refund" value="100%" hint="If AI fails" />
        </div>

        {/* Transactions */}
        <div className="au-glass au-pad">
          <h2 style={{
            fontSize: 18, marginTop: 0, marginBottom: 18, fontWeight: 700,
            display: "flex", alignItems: "center", gap: 8
          }}>
            <BarChart3 size={20} style={{ color: "var(--au-accent-ink)" }} /> Transaction History
          </h2>

          {transactions.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "var(--au-muted)" }}>
              No transactions yet. Start using credits to see history here.
            </div>
          ) : (
            <div>
              {transactions.slice(txPage * TX_PER_PAGE, txPage * TX_PER_PAGE + TX_PER_PAGE).map((tx, i) => (
                <TxRow key={txPage * TX_PER_PAGE + i} tx={tx} />
              ))}
              <Pagination page={txPage} setPage={setTxPage} total={transactions.length} pageSize={TX_PER_PAGE} />
            </div>
          )}
        </div>

        {/* Footer note */}
        <div className="au-glass" style={{
          marginTop: 28, padding: 16, fontSize: 12, color: "var(--au-text-2)", lineHeight: 1.7,
          display: "flex", gap: 10, alignItems: "flex-start"
        }}>
          <Info size={16} style={{ color: "var(--au-accent-ink)", flex: "none", marginTop: 2 }} />
          <span>
            <strong>How credits work:</strong> Each AI-powered scan deducts credits based on the AI provider you choose.
            Rule-based scans are always FREE. Failed scans auto-refund. Credits never expire.
          </span>
        </div>
      </div>

      {/* Add Credits Modal */}
      {showAddModal && (
        <div role="dialog" aria-modal="true" style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1000, padding: 20
        }} onClick={() => setShowAddModal(false)}>
          <div onClick={e => e.stopPropagation()} className="au-glass" style={{
            maxWidth: 520, width: "100%", maxHeight: "88vh",
            display: "flex", flexDirection: "column", overflow: "hidden"
          }}>
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "22px 26px 14px", borderBottom: "1px solid var(--au-hair)", flex: "none"
            }}>
              <h2 style={{ margin: 0, fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
                <CreditCard size={20} style={{ color: "var(--au-accent-ink)" }} /> Choose Package
              </h2>
              <button onClick={() => setShowAddModal(false)} aria-label="Close" className="au-pill au-pill-ghost au-pill-sm" style={{ padding: 8 }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: "16px 26px 22px", overflowY: "auto" }}>
            {!razorpayKeyId && (
              <div style={{
                padding: 14, background: "var(--au-glass-strong)",
                border: "1px solid var(--au-hair)", borderRadius: "var(--au-r-input)",
                fontSize: 13, color: "var(--au-warn)", marginBottom: 18,
                display: "flex", gap: 8, alignItems: "center"
              }}>
                <AlertTriangle size={16} style={{ flex: "none" }} /> Payment gateway coming soon. We're finalizing setup.
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {packages.map(pkg => (
                <div key={pkg.id} className="au-glass" style={{
                  padding: 14, borderRadius: "var(--au-r-sm)"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "var(--au-text)", marginBottom: 4 }}>
                        {pkg.name}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--au-muted)" }}>
                        {pkg.credits.toLocaleString()} credits
                        {pkg.bonus_pct > 0 && (
                          <span style={{ color: "var(--au-success)", marginLeft: 8, fontWeight: 700 }}>
                            +{pkg.bonus_pct}% bonus
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: "var(--au-accent-ink)" }}>
                        ${(pkg.price_usd ?? 0).toLocaleString()}
                      </div>
                      {pkg.price_inr != null && (
                        <div style={{ fontSize: 11, color: "var(--au-muted)", marginTop: 2 }}>
                          ≈ ₹{pkg.price_inr.toLocaleString()} at checkout
                        </div>
                      )}
                      <button
                        className={razorpayKeyId ? "au-pill au-pill-primary au-pill-sm" : "au-pill au-pill-glass au-pill-sm"}
                        onClick={() => initiatePurchase(pkg.id)}
                        disabled={busy || !razorpayKeyId}
                        style={{
                          marginTop: 8,
                          cursor: razorpayKeyId ? "pointer" : "not-allowed",
                          opacity: razorpayKeyId ? 1 : 0.5
                        }}>
                        {razorpayKeyId ? "BUY" : "SOON"}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{
              marginTop: 20, fontSize: 11, color: "var(--au-muted)", textAlign: "center",
              display: "flex", gap: 6, alignItems: "center", justifyContent: "center"
            }}>
              <Lock size={12} /> Prices in USD · charged in ₹ (INR) at checkout · Invoice provided
            </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoCard({ Icon, label, value, hint }) {
  return (
    <div className="au-glass" style={{ padding: "16px 18px" }}>
      <div style={{
        fontSize: 10, color: "var(--au-muted)", letterSpacing: 2, marginBottom: 6,
        display: "flex", alignItems: "center", gap: 6
      }}>
        {Icon && <Icon size={12} style={{ color: "var(--au-accent-ink)" }} />} {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 800, color: "var(--au-accent-ink)", marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 11, color: "var(--au-muted)" }}>{hint}</div>
    </div>
  );
}

function TxRow({ tx }) {
  const isSub = tx.transaction_type === "SUBSCRIPTION";
  const isCredit = tx.transaction_type === "PURCHASE" || tx.transaction_type === "REFUND" || tx.transaction_type === "BONUS";
  const sign = isCredit ? "+" : "−";
  const color = isCredit ? "var(--au-success)" : "var(--au-error)";
  const Icon = {
    PURCHASE: CreditCard,
    DEBIT: Send,
    REFUND: RotateCcw,
    BONUS: Gift,
    SUBSCRIPTION: CreditCard,
  }[tx.transaction_type] || Coins;
  const paidInr = tx.razorpay_amount_paise ? tx.razorpay_amount_paise / 100 : null;

  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "12px 0", borderBottom: "1px solid var(--au-hair)"
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Icon size={20} style={{ color: "var(--au-accent-ink)", flex: "none" }} />
        <div>
          <div style={{ fontSize: 14, color: "var(--au-text)", fontWeight: 600 }}>{tx.reason}</div>
          <div style={{ fontSize: 11, color: "var(--au-muted)" }}>
            {new Date(tx.created_at).toLocaleString()}
          </div>
          {tx.invoice_number && (
            <button onClick={() => downloadInvoice(tx.invoice_number)}
              style={{ marginTop: 4, background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--au-accent-ink)", fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }}>
              <FileText size={12} /> Download receipt
            </button>
          )}
        </div>
      </div>
      <div style={{ textAlign: "right" }}>
        {isSub ? (
          <>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--au-accent-ink)" }}>
              {paidInr != null ? `₹${paidInr.toLocaleString()}` : "PAID"}
            </div>
            <div style={{ fontSize: 11, color: "var(--au-muted)" }}>subscription</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 16, fontWeight: 700, color }}>
              {sign}{tx.amount}
            </div>
            <div style={{ fontSize: 11, color: "var(--au-muted)" }}>
              Balance: {tx.balance_after}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
